import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import useCanonicalTag from '../hooks/useCanonicalTag';
import useMetaTags from '../hooks/useMetaTags';
import { getChampionshipDetails, getChampionshipStandings } from '../services/api';
import '../styles/fonts.css';
import '../styles/championship_detail.css';
import '../styles/league_detail.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DualScrollTable from '../components/DualScrollTable';

function fmtPts(n) {
  if (n === null || n === undefined) return '—';
  return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(1);
}

function ChampionshipDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US';
  const [championship, setChampionship] = useState(null);
  usePageTitle(championship?.name);
  useCanonicalTag();
  useMetaTags({
    title: championship ? `${championship.name} | Celeritas` : 'Celeritas',
    description: championship?.name ? `Championship standings and results for ${championship.name} on Celeritas` : 'Championship standings and results on Celeritas',
    url: `https://celeritas.live/championships/${id}`
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [standings, setStandings] = useState([]);
  const [standingsMeta, setStandingsMeta] = useState(null);
  const [standingsLoading, setStandingsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pilotos-geral');
  const [activeClassTab, setActiveClassTab] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('pilotos');

  useEffect(() => {
    setLoading(true);
    setStandingsLoading(true);
    getChampionshipDetails(id)
      .then(data => {
        setChampionship(data);
        setError(null);
        return getChampionshipStandings(data.championship_id)
          .then(sData => {
            setStandings(sData.standings || []);
            setStandingsMeta(sData);
          })
          .catch(() => {})
          .finally(() => setStandingsLoading(false));
      })
      .catch(err => {
        console.error("Erro ao carregar campeonato:", err);
        setError(t('championshipDetail.error'));
        setStandingsLoading(false);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const computeTeamStandings = (entries) => {
    const driverTeams = standingsMeta?.driver_teams || {};
    if (Object.keys(driverTeams).length === 0) return [];
    const usesDrops = standingsMeta?.team_scoring_uses_driver_drops !== false;
    const usesOverall = standingsMeta?.team_scoring_uses_overall_position === true;

    const teamsMap = {};
    entries.forEach(entry => {
      const team = driverTeams[String(entry.cust_id)];
      if (!team) return;
      if (!teamsMap[team]) {
        teamsMap[team] = { name: team, drivers: [], total_points: 0, wins: 0, top5s: 0, top10s: 0, incidents: 0 };
      }
      const pts = usesOverall && entry.team_total_points != null
        ? entry.team_total_points
        : usesDrops
          ? (entry.total_points || 0)
          : (entry.race_points || []).reduce((sum, p) => sum + (p ?? 0), 0);
      teamsMap[team].drivers.push(entry.display_name);
      teamsMap[team].total_points += pts;
      teamsMap[team].wins += entry.wins || 0;
      teamsMap[team].top5s += entry.top5s || 0;
      teamsMap[team].top10s += entry.top10s || 0;
      teamsMap[team].incidents += entry.incidents || 0;
    });

    return Object.values(teamsMap)
      .sort((a, b) => b.total_points - a.total_points)
      .map((t, i) => ({ ...t, position: i + 1 }));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const teamStandings = useMemo(() => computeTeamStandings(standings), [standings, standingsMeta]);

  const hasTeams = teamStandings.length > 0;

  if (loading) return (
    <div className="ChampionshipDetailPage">
      <Header />
      <main><section className="center-col"><p style={{ color: 'white', fontSize: '20px', textAlign: 'center' }}>{t('championshipDetail.loading')}</p></section></main>
      <Footer />
    </div>
  );

  if (error) return (
    <div className="ChampionshipDetailPage">
      <Header />
      <main><section className="center-col"><p className="error-message" style={{ color: 'red', fontSize: '20px', textAlign: 'center' }}>{error}</p></section></main>
      <Footer />
    </div>
  );

  if (!championship) return (
    <div className="ChampionshipDetailPage">
      <Header />
      <main><section className="center-col"><p style={{ color: 'white', fontSize: '20px', textAlign: 'center' }}>{t('championshipDetail.notFound')}</p></section></main>
      <Footer />
    </div>
  );

  const isMultiClass = !!(standingsMeta?.standings_by_class && Object.keys(standingsMeta.standings_by_class).length > 0);
  const classNames = isMultiClass ? Object.keys(standingsMeta.standings_by_class) : [];
  const usesOverallForTeams = standingsMeta?.team_scoring_uses_overall_position === true;
  const currentClassTab = activeClassTab || classNames[0] || null;

  const now = new Date();
  const sessions = (championship.sessions || [])
    .filter(s => (s.race_length_mins || 0) > 0 || !s.subsession_id)
    .slice().sort(
      (a, b) => new Date(a.start_time) - new Date(b.start_time)
    );

  const specificClasses = (championship.driver_points_car_classes || []).filter(
    cls => cls.name !== 'All Car Classes'
  );
  const carNames = specificClasses.length > 0
    ? [...new Set(specificClasses.flatMap(cls => (cls.cars_in_class || []).filter(c => c.car_id !== 0).map(c => c.car_name)))]
    : [...new Set((championship.points_cars || []).filter(c => c.car_id !== 0).map(c => c.car_name))];

  const nativePointsBanner = standingsMeta?.use_native_points ? (
    <p style={{ fontSize: '12px', color: '#9ba3ab', marginBottom: '12px', padding: '6px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: '4px', borderLeft: '3px solid #4caf50' }}>
      {t('championshipDetail.nativeNote')}
    </p>
  ) : null;

  const noPointsWarning = standingsMeta && !standingsMeta.has_custom_points && !standingsMeta.use_native_points
    ? <p className="standings-no-points-warning">{t('championshipDetail.noPointsConfig')}</p>
    : null;

  const driverTeams = standingsMeta?.driver_teams || {};
  const hasTeamCol = Object.keys(driverTeams).length > 0;

  const renderAggTable = (entries, useClassPos = false) => {
    const leaderPts = entries[0]?.total_points ?? 0;
    return (
      <DualScrollTable>
        <table className="standings-table standings-table-wide">
          <thead>
            <tr>
              <th className="standings-th-pos">{t('common.pos')}</th>
              <th className="standings-th-driver">{t('common.driver')}</th>
              {hasTeamCol && <th className="standings-th-driver">{t('common.team')}</th>}
              <th className="standings-th-pts" title={t('common.races')}>{t('common.races')}</th>
              <th className="standings-th-pts" title={t('common.points')}>{t('common.points')}</th>
              <th className="standings-th-pts" title={t('common.bonus')}>{t('common.bonus')}</th>
              <th className="standings-th-pts" title={t('common.penalty')}>{t('common.penalty')}</th>
              <th className="standings-th-pts" title={t('common.incidents')}>{t('common.incidents')}</th>
              <th className="standings-th-pts" title={t('common.wins')}>{t('common.wins')}</th>
              <th className="standings-th-pts" title="Top 5">Top 5</th>
              <th className="standings-th-pts" title="Top 10">Top 10</th>
              <th className="standings-th-pts" title={t('common.poles')}>{t('common.poles')}</th>
              <th className="standings-th-pts" title={t('championshipDetail.gapToLeader')}>{t('championshipDetail.gapToLeader')}</th>
              <th className="standings-th-pts" title={t('championshipDetail.gapToNext')}>{t('championshipDetail.gapToNext')}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, i) => {
              const pos = useClassPos ? (entry.class_position ?? entry.position) : entry.position;
              const gapToLeader = i === 0 ? 0 : leaderPts - entry.total_points;
              const prevPts = i > 0 ? entries[i - 1].total_points : entry.total_points;
              const gapToPrev = i === 0 ? 0 : prevPts - entry.total_points;
              const team = driverTeams[String(entry.cust_id)] || '';
              return (
                <tr key={entry.cust_id} className={i === 0 ? 'standings-leader' : ''}>
                  <td className="standings-pos">{pos}º</td>
                  <td className="standings-driver">{entry.display_name}</td>
                  {hasTeamCol && <td className="standings-driver">{team || <span style={{ opacity: 0.3 }}>—</span>}</td>}
                  <td className="standings-pts">{entry.races}</td>
                  <td className="standings-pts">{fmtPts(entry.total_points)}</td>
                  <td className="standings-pts">{fmtPts(entry.total_bonus_points ?? 0)}</td>
                  <td className="standings-pts">
                    {(entry.total_penalty_points ?? 0) > 0 ? `-${fmtPts(entry.total_penalty_points)}` : '—'}
                  </td>
                  <td className="standings-pts">{entry.incidents ?? 0}</td>
                  <td className="standings-pts">{entry.wins ?? 0}</td>
                  <td className="standings-pts">{entry.top5s ?? 0}</td>
                  <td className="standings-pts">{entry.top10s ?? 0}</td>
                  <td className="standings-pts">{entry.poles ?? 0}</td>
                  <td className="standings-pts standings-gap">
                    {gapToLeader === 0 ? '—' : `-${fmtPts(gapToLeader)}`}
                  </td>
                  <td className="standings-pts standings-gap">
                    {gapToPrev === 0 ? '—' : `-${fmtPts(gapToPrev)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </DualScrollTable>
    );
  };

  const renderTeamsTable = (teamsList) => (
    <DualScrollTable>
      <table className="standings-table standings-table-wide">
        <thead>
          <tr>
            <th className="standings-th-pos">{t('common.pos')}</th>
            <th className="standings-th-driver">{t('common.team')}</th>
            <th className="standings-th-driver">{t('championshipDetail.driversTab')}</th>
            <th className="standings-th-pts" title={t('common.wins')}>{t('common.wins')}</th>
            <th className="standings-th-pts" title="Top 5">Top 5</th>
            <th className="standings-th-pts" title="Top 10">Top 10</th>
            <th className="standings-th-pts" title={t('common.incidents')}>{t('common.incidents')}</th>
            <th className="standings-th-pts">{t('common.points')}</th>
          </tr>
        </thead>
        <tbody>
          {teamsList.map((team, i) => (
            <tr key={team.name} className={i === 0 ? 'standings-leader' : ''}>
              <td className="standings-pos">{team.position}º</td>
              <td className="standings-driver">{team.name}</td>
              <td className="team-standings-driver-list">{team.drivers.join(', ')}</td>
              <td className="standings-pts">{team.wins}</td>
              <td className="standings-pts">{team.top5s}</td>
              <td className="standings-pts">{team.top10s}</td>
              <td className="standings-pts">{team.incidents}</td>
              <td className="standings-pts">{fmtPts(team.total_points)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </DualScrollTable>
  );

  // ── Tab: Overall standings ────────────────────────────────────────────────────
  const renderPilotosGeralTab = () => {
    if (standingsLoading) return <p className="empty-state">{t('championshipDetail.loadingStandings')}</p>;
    if (noPointsWarning) return noPointsWarning;
    if (standings.length === 0) return <p className="empty-state">{t('championshipDetail.noResults')}</p>;
    return <>{nativePointsBanner}{renderAggTable(standings, false)}</>;
  };

  // ── Tab: By category ─────────────────────────────────────────────────────────
  const renderPilotosCategoriaTab = () => {
    if (standingsLoading) return <p className="empty-state">{t('championshipDetail.loadingStandings')}</p>;
    if (noPointsWarning) return noPointsWarning;
    if (standings.length === 0) return <p className="empty-state">{t('championshipDetail.noResults')}</p>;
    if (!standingsMeta?.standings_by_class) return <p className="empty-state">{t('championshipDetail.noCategories')}</p>;
    return (
      <>{nativePointsBanner}{Object.entries(standingsMeta.standings_by_class).map(([className, classEntries]) => (
        <div key={className} style={{ marginBottom: 24 }}>
          <div className="box-header" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12 }}>
            <h4 style={{ margin: 0, fontSize: '0.95rem', opacity: 0.85 }}>{className}</h4>
            <span className="box-header-count">{classEntries.length} {t('championshipDetail.drivers')}</span>
          </div>
          {renderAggTable(classEntries, true)}
        </div>
      ))}</>
    );
  };

  // ── Tab: Race-by-race ─────────────────────────────────────────────────────────
  const renderRaceByRaceTab = () => {
    if (standingsLoading) return <p className="empty-state">{t('common.loading')}</p>;
    if (noPointsWarning) return noPointsWarning;
    if (standings.length === 0) return <p className="empty-state">{t('championshipDetail.noResults')}</p>;

    const simsessionNames = standingsMeta?.simsession_names || [];

    if ((standingsMeta?.use_driver_categories || standingsMeta?.use_car_class_scoring) && standingsMeta?.standings_by_class) {
      return (<>{nativePointsBanner}{Object.entries(standingsMeta.standings_by_class).map(([className, classEntries]) => (
        <div key={className} style={{ marginBottom: 24 }}>
          <div className="box-header" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12 }}>
            <h4 style={{ margin: 0, fontSize: '0.95rem', opacity: 0.85 }}>{className}</h4>
            <span className="box-header-count">{classEntries.length} {t('championshipDetail.drivers')}</span>
          </div>
          <StandingsTable entries={classEntries} sessions={standingsMeta?.sessions || []} simsessionNames={simsessionNames} useClassPos />
        </div>
      ))}</>);
    }

    return <>{nativePointsBanner}<StandingsTable entries={standings} sessions={standingsMeta?.sessions || []} simsessionNames={simsessionNames} /></>;
  };

  // ── Subtab multi-class: Race-by-race per class ────────────────────────────────
  const renderClassRaceByRaceTab = (className) => {
    if (standingsLoading) return <p className="empty-state">{t('common.loading')}</p>;
    if (noPointsWarning) return noPointsWarning;
    const classEntries = standingsMeta?.standings_by_class?.[className] || [];
    if (classEntries.length === 0) return <p className="empty-state">{t('championshipDetail.noResults')}</p>;
    const simsessionNames = standingsMeta?.simsession_names || [];
    return <>{nativePointsBanner}<StandingsTable entries={classEntries} sessions={standingsMeta?.sessions || []} simsessionNames={simsessionNames} useClassPos /></>;
  };

  // ── Subtab multi-class: Teams per class ──────────────────────────────────────
  const renderClassTeamsTab = (className) => {
    if (standingsLoading) return <p className="empty-state">{t('common.loading')}</p>;
    const classEntries = standingsMeta?.standings_by_class?.[className] || [];
    const classTeamStandings = computeTeamStandings(classEntries);
    if (classTeamStandings.length === 0) return <p className="empty-state">{t('championshipDetail.noTeamsClass')}</p>;
    return renderTeamsTable(classTeamStandings);
  };

  // ── Tab: Teams ────────────────────────────────────────────────────────────────
  const renderTeamsTab = () => {
    if (standingsLoading) return <p className="empty-state">{t('common.loading')}</p>;
    if (!hasTeams) return <p className="empty-state">{t('championshipDetail.noTeams')}</p>;

    return (
      <>
      {standingsMeta?.team_scoring_uses_overall_position && (
        <p style={{ fontSize: '12px', color: '#9ba3ab', marginBottom: '12px', padding: '6px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: '4px', borderLeft: '3px solid #4a90d9' }}>
          {t('championshipDetail.teamOverallNote')}
        </p>
      )}
      {renderTeamsTable(teamStandings)}
      </>
    );
  };

  return (
    <div className="ChampionshipDetailPage">
      <Header />

      <main>
        <div className="content-grid">
          <section className="center-col championship-page">
            <nav className="breadcrumb">
              <Link to="/championships">{t('common.championships')}</Link> &gt; <Link to={`/leagues/${championship.league_league_id}`}>{championship.league_name}</Link> &gt; <span>{championship.name}</span>
            </nav>

            <section className="league-info-bar" style={championship.regulation_text ? { flexDirection: 'row', alignItems: 'stretch', gap: 0 } : {}}>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 15 }}>
                <div className="league-header">
                  <img
                    src={championship.league_logo || '/placeholder-logo.png'}
                    alt={championship.league_name}
                    className="league-logo-header"
                  />
                  <div>
                    <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.7 }}>{championship.league_name}</p>
                    <h1 style={{ marginBottom: championship.league_managed ? 6 : 0 }}>{championship.name}</h1>
                    {championship.league_managed && (
                      <span title={t('nav.verifiedTooltip')} style={{
                        fontSize: '0.75rem', fontWeight: 600,
                        background: '#1a3d2b', color: '#4caf50',
                        border: '1px solid #2d6b3a', borderRadius: 4,
                        padding: '2px 9px', cursor: 'default', display: 'inline-block',
                      }}>✓ {t('nav.verifiedBadge')}</span>
                    )}
                  </div>
                </div>

                {carNames.length > 0 && (
                  <div className="league-about">
                    <h3>{t('championshipDetail.cars')}</h3>
                    <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                      {carNames.map((name, i) => <li key={i}>{name}</li>)}
                    </ul>
                  </div>
                )}

                <div className="champ-meta-row">
                  <p><strong>{t('championshipDetail.stages')}:</strong> {sessions.length}</p>
                  <p><strong>{t('championshipDetail.drops')}</strong> {championship.num_drops}</p>
                  <p>
                    <strong>{t('common.status')}:</strong>{' '}
                    <span className={championship.active ? 'status-live' : 'status-finished'}>
                      {championship.active ? t('common.active') : t('common.finished')}
                    </span>
                  </p>
                </div>
              </div>

              {championship.regulation_text && (
                <div style={{
                  width: 320, minWidth: 220, borderLeft: '1px solid rgba(255,255,255,0.1)',
                  marginLeft: 20, paddingLeft: 20, overflowY: 'auto',
                  maxHeight: 200, fontSize: '0.82rem', lineHeight: 1.6,
                  color: 'rgba(255,255,255,0.75)', whiteSpace: 'pre-wrap',
                }}>
                  <p style={{ margin: '0 0 6px', fontSize: '0.75rem', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('championshipDetail.regulation')}
                  </p>
                  {championship.regulation_text}
                </div>
              )}
            </section>

            <div className="champ-body-grid">

              {/* Widget promo banner */}
              <div style={{ gridColumn: '1 / -1', background: '#161b22', border: '1px solid #2d2a1e', borderRadius: 6, padding: '8px 16px', textAlign: 'center', fontSize: '0.82rem' }}>
                <span style={{ color: '#e8c84a' }}>{t('widget.promoBanner')}{' '}</span>
                <Link to="/widget-info" style={{ color: '#e8c84a', textDecoration: 'underline', fontWeight: 700 }}>{t('widget.promoLink')}</Link>
              </div>

              {/* LEFT COLUMN — Standings with tabs */}
              <div className="champ-standings-col">
                <div className="box-header">
                  <h3>{t('common.standings')}</h3>
                  {standings.length > 0 && (
                    <span className="box-header-count">{standings.length} {t('championshipDetail.drivers')}</span>
                  )}
                </div>

                {isMultiClass ? (
                  <div>
                    <div className="standings-tabs">
                      {classNames.map(cls => (
                        <button
                          key={cls}
                          className={`standings-tab${currentClassTab === cls ? ' active' : ''}`}
                          onClick={() => setActiveClassTab(cls)}
                        >
                          {cls}
                        </button>
                      ))}
                      {hasTeams && usesOverallForTeams && (
                        <button
                          className={`standings-tab${currentClassTab === '__equipes__' ? ' active' : ''}`}
                          onClick={() => setActiveClassTab('__equipes__')}
                        >
                          {t('championshipDetail.teams')}
                        </button>
                      )}
                    </div>
                    {currentClassTab === '__equipes__' ? (
                      <div style={{ paddingTop: 8 }}>{renderTeamsTab()}</div>
                    ) : (
                      <>
                        <div className="standings-subtabs">
                          <button
                            className={`standings-subtab${activeSubTab === 'pilotos' ? ' active' : ''}`}
                            onClick={() => setActiveSubTab('pilotos')}
                          >
                            {t('championshipDetail.driversTab')}
                          </button>
                          <button
                            className={`standings-subtab${activeSubTab === 'race' ? ' active' : ''}`}
                            onClick={() => setActiveSubTab('race')}
                          >
                            {t('championshipDetail.raceByRace')}
                          </button>
                          {hasTeams && !usesOverallForTeams && (
                            <button
                              className={`standings-subtab${activeSubTab === 'teams' ? ' active' : ''}`}
                              onClick={() => setActiveSubTab('teams')}
                            >
                              {t('championshipDetail.teams')}
                            </button>
                          )}
                        </div>
                        <div style={{ paddingTop: 8 }}>
                          {currentClassTab && activeSubTab === 'pilotos' && renderAggTable(standingsMeta?.standings_by_class?.[currentClassTab] || [], true)}
                          {currentClassTab && activeSubTab === 'race' && renderClassRaceByRaceTab(currentClassTab)}
                          {currentClassTab && activeSubTab === 'teams' && renderClassTeamsTab(currentClassTab)}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="standings-tabs">
                      <button
                        className={`standings-tab${activeTab === 'pilotos-geral' ? ' active' : ''}`}
                        onClick={() => setActiveTab('pilotos-geral')}
                      >
                        {t('championshipDetail.overallStandings')}
                      </button>
                      {standingsMeta?.use_driver_categories && (
                        <button
                          className={`standings-tab${activeTab === 'pilotos-cat' ? ' active' : ''}`}
                          onClick={() => setActiveTab('pilotos-cat')}
                        >
                          {t('championshipDetail.byCategory')}
                        </button>
                      )}
                      <button
                        className={`standings-tab${activeTab === 'race' ? ' active' : ''}`}
                        onClick={() => setActiveTab('race')}
                      >
                        {t('championshipDetail.raceByRace')}
                      </button>
                      {hasTeams && (
                        <button
                          className={`standings-tab${activeTab === 'teams' ? ' active' : ''}`}
                          onClick={() => setActiveTab('teams')}
                        >
                          {t('championshipDetail.teams')}
                        </button>
                      )}
                    </div>
                    <div style={{ paddingTop: 8 }}>
                      {activeTab === 'pilotos-geral' && renderPilotosGeralTab()}
                      {activeTab === 'pilotos-cat' && renderPilotosCategoriaTab()}
                      {activeTab === 'race' && renderRaceByRaceTab()}
                      {activeTab === 'teams' && renderTeamsTab()}
                    </div>
                  </>
                )}
              </div>

              {/* RIGHT COLUMN — Schedule */}
              <div className="champ-schedule-col">
                <div className="box-header">
                  <h3>{t('championshipDetail.stages')}</h3>
                  <span className="box-header-count">{sessions.length} {t('common.races').toLowerCase()}</span>
                </div>
                {sessions.length > 0 ? (
                  <div className="schedule-list">
                    {sessions.map((session, i) => {
                      const completed = session.start_time && new Date(session.start_time) < now;
                      return (
                        <div
                          key={session.subsession_id}
                          className={`schedule-row ${completed ? 'schedule-done' : 'schedule-upcoming'}`}
                        >
                          <span className="schedule-num">{i + 1}</span>
                          <div className="schedule-info">
                            <span className="schedule-track">{session.track_name || t('organizer.trackNotDefined')}</span>
                            {session.start_time && (
                              <span className="schedule-date">
                                {new Date(session.start_time).toLocaleDateString(locale, {
                                  day: '2-digit', month: 'short', year: 'numeric'
                                }).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="schedule-action">
                            {completed ? (
                              <Link to={`/races/${session.subsession_id}`} className="btn-results">
                                {t('common.result')}
                              </Link>
                            ) : (
                              <span className="badge-next">{t('common.comingSoon')}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="empty-state">{t('championshipDetail.noStages')}</p>
                )}
              </div>

            </div>

          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function StandingsTable({ entries, sessions, useClassPos = false, simsessionNames = [] }) {
  const { t } = useTranslation();
  const isMultiRace = simsessionNames.length > 1;
  const colsPerSession = isMultiRace ? 3 + simsessionNames.length : 4;

  return (
    <DualScrollTable>
      <table className="standings-table standings-table-wide">
        <thead>
          <tr>
            <th className="standings-th-pos" rowSpan={2}>{t('common.pos')}</th>
            <th className="standings-th-driver" rowSpan={2}>{t('common.driver')}</th>
            {sessions.map((s, i) => (
              <th
                key={i}
                className="standings-race-group-header"
                colSpan={colsPerSession}
                title={[s.track_name, s.config_name].filter(Boolean).join(' — ')}
              >
                {t('championshipDetail.stage')} {i + 1}
              </th>
            ))}
            <th className="standings-th-pts" rowSpan={2}>{t('common.points')}</th>
          </tr>
          <tr>
            {sessions.map((_, i) => (
              <React.Fragment key={i}>
                <th className="standings-sub-col standings-sub-T" title="Total">T</th>
                {isMultiRace ? (
                  simsessionNames.map((simName, hi) => (
                    <th key={hi} className="standings-sub-col standings-sub-R" title={simName}>
                      H{hi + 1}
                    </th>
                  ))
                ) : (
                  <th className="standings-sub-col standings-sub-R" title="Race points">R</th>
                )}
                <th className="standings-sub-col standings-sub-B" title={t('common.bonus')}>B</th>
                <th className="standings-sub-col standings-sub-P" title={t('common.penalty')}>P</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, i) => (
            <tr key={entry.cust_id || i} className={i === 0 ? 'standings-leader' : ''}>
              <td className="standings-pos">{useClassPos ? (entry.class_position ?? entry.position) : entry.position}º</td>
              <td className="standings-driver">{entry.display_name}</td>
              {sessions.map((_, j) => {
                const pts = (entry.race_points || [])[j];
                const bd = (entry.race_breakdown || [])[j];
                const hbd = (entry.race_heat_breakdown || [])[j];
                const dropped = (entry.dropped_indexes || []).includes(j);
                const isNone = pts === null || pts === undefined;
                const baseClass = `standings-sub-td ${dropped ? 'standings-dropped' : ''} ${isNone ? 'standings-no-race' : ''}`;
                return (
                  <React.Fragment key={j}>
                    <td className={`${baseClass} standings-sub-T`}>
                      {isNone ? '—' : fmtPts(pts)}
                    </td>
                    {isMultiRace ? (
                      simsessionNames.map((simName, hi) => (
                        <td key={hi} className={`${baseClass} standings-sub-R`}>
                          {isNone ? '' : (hbd?.[simName] != null ? fmtPts(hbd[simName]) : '—')}
                        </td>
                      ))
                    ) : (
                      <td className={`${baseClass} standings-sub-R`}>
                        {isNone ? '' : fmtPts(bd?.r ?? 0)}
                      </td>
                    )}
                    <td className={`${baseClass} standings-sub-B`}>
                      {isNone ? '' : (bd?.b > 0 ? fmtPts(bd.b) : '')}
                    </td>
                    <td className={`${baseClass} standings-sub-P`}>
                      {isNone ? '' : (bd?.p > 0 ? `-${fmtPts(bd.p)}` : '')}
                    </td>
                  </React.Fragment>
                );
              })}
              <td className="standings-pts">
                {fmtPts(entry.total_points)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </DualScrollTable>
  );
}

export default ChampionshipDetail;
